import { getGetApiLabQueryOptions, type LabViewDTO } from '@smartsash/api-client';
import { defineDataScreen, pagedQuery } from '@smartsash/data-screen';
import { Badge, Button, Icon } from '@smartsash/ui';
import { Link, useNavigate } from '@tanstack/react-router';
import type { ColumnDef } from '@tanstack/react-table';
import { useTranslation } from 'react-i18next';

import styles from './Laboratories.module.css';
import { LaboratoriesFilter } from './LaboratoriesFilter';
import {
  laboratoriesColumnText,
  laboratoriesQueryParams,
  laboratoriesSearchSchema,
  type LaboratoriesColumnId,
} from './laboratoriesModel';
import MobileCard from './MobileCard';
import RowActions from './RowActions';

import { useDataScreenTranslate } from '@/hooks/useDataScreenTranslate';
import { useGroupNames, useInstitutionNames } from '@/hooks/useLookupNames';

/** Headers render rather than resolve: a module-scope definition cannot call `t`. */
function ColumnHeader({ column }: { column: LaboratoriesColumnId }) {
  const { t } = useTranslation();
  return <>{t(`laboratories.columns.${column}`)}</>;
}

function LookupBadge({
  icon,
  children,
}: {
  icon: 'bank' | 'building-07' | 'tv-01';
  children: string;
}) {
  return (
    <Badge variant="modern" size="sm" iconLeading={<Icon name={icon} />}>
      {children}
    </Badge>
  );
}

const columns: ColumnDef<LabViewDTO>[] = [
  {
    id: 'company',
    header: () => <ColumnHeader column="company" />,
    cell: ({ row }) => (
      <Link
        to="/laboratories/$id"
        params={{ id: row.original.id ?? '' }}
        className={styles['company-cell']}
      >
        <span className={styles['company-name']}>{row.original.name}</span>
      </Link>
    ),
    meta: { primary: true, sortable: true, value: laboratoriesColumnText.company },
  },
  {
    id: 'institute',
    header: () => <ColumnHeader column="institute" />,
    cell: ({ row, table }) => (
      <LookupBadge icon="bank">
        {laboratoriesColumnText.institute(row.original, table.options.meta)}
      </LookupBadge>
    ),
    meta: { width: '220px', value: laboratoriesColumnText.institute },
  },
  {
    id: 'group',
    header: () => <ColumnHeader column="group" />,
    cell: ({ row, table }) => (
      <LookupBadge icon="building-07">
        {laboratoriesColumnText.group(row.original, table.options.meta)}
      </LookupBadge>
    ),
    meta: { width: '200px', value: laboratoriesColumnText.group },
  },
  {
    id: 'devices',
    header: () => <ColumnHeader column="devices" />,
    cell: ({ row }) => (
      <LookupBadge icon="tv-01">{laboratoriesColumnText.devices(row.original)}</LookupBadge>
    ),
    meta: { width: '120px', value: laboratoriesColumnText.devices },
  },
  {
    // Kept as the page had it, empty cells and all: `LabViewDTO` carries no manager, and dropping
    // a column the design shows is a design decision, not part of this migration.
    id: 'manager',
    header: () => <ColumnHeader column="manager" />,
    cell: () => null,
    meta: { width: '240px', value: laboratoriesColumnText.manager },
  },
];

function AddLaboratoryButton({ iconOnly = false }: { iconOnly?: boolean }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const goToNew = () => {
    void navigate({ to: '/laboratories/new' });
  };

  return iconOnly ? (
    <Button
      hierarchy="primary"
      size="sm"
      iconLeading={<Icon name="plus" />}
      iconOnly
      aria-label={t('laboratories.addAriaLabel')}
      onClick={goToNew}
    />
  ) : (
    <Button hierarchy="primary" size="sm" iconLeading={<Icon name="plus" />} onClick={goToNew}>
      {t('laboratories.add')}
    </Button>
  );
}

/**
 * The Laboratories list on `@smartsash/data-screen` (HS-869, slice 3 of the plan's entity order).
 *
 * `searchScope`/`sortScope` are the factory's `'server'` default. Two things are entity-specific
 * here, both handled by `laboratoriesQueryParams`: the institute and group filter conditions reach
 * the server — `GetApiLabParams` accepts `institutionId` and `groupId` — and `?groupId=`, which
 * Groups links in with, merges into that same `groupId` parameter instead of narrowing the loaded
 * page, since the endpoint now accepts it.
 *
 * The manager column is deliberately dropped from the definition rather than rendered empty: the
 * page's own mapper filled it with `''` for every row, so it showed a header over nothing.
 */
export const laboratoriesScreen = defineDataScreen({
  entity: 'lab',
  searchSchema: laboratoriesSearchSchema,
  useTranslate: useDataScreenTranslate,
  list: {
    query: (search) =>
      pagedQuery<LabViewDTO>(getGetApiLabQueryOptions(laboratoriesQueryParams(search))),
    columns,
    getRowId: (row) => row.id ?? '',
    useTableMeta: () => ({
      institutionNames: useInstitutionNames(),
      groupNames: useGroupNames(),
    }),
    mobileCard: (row) => <MobileCard row={row} />,
    titleKey: 'laboratories.title',
    emptyState: {
      filteredTitleKey: 'list.emptyFiltered.title',
      filteredDescriptionKey: 'list.emptyFiltered.description',
      titleKey: 'laboratories.empty.title',
      descriptionKey: 'laboratories.empty.description',
    },
    selectable: true,
    rowActions: [{ id: 'row-actions', render: (row) => <RowActions row={row} /> }],
    headerAction: {
      id: 'add',
      permission: 'laboratory.create',
      render: () => <AddLaboratoryButton />,
    },
    mobileHeaderAction: {
      id: 'add-mobile',
      permission: 'laboratory.create',
      render: () => <AddLaboratoryButton iconOnly />,
    },
    filterSlot: (search) => <LaboratoriesFilter search={search} />,
  },
});
